import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import { useFullHeightLayout } from '../../components/layout/AppShell';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';
import type { CalendarPost, PostPlatform, PostStatus, PostSummary } from '../../types/publishing';
import {
  bucketRowsByDay,
  buildMonthGrid,
  distinctPostCount,
  expandPostToRows,
  filterPosts,
  MAX_CELL_ROWS,
  unscheduledPosts,
  type CalendarCell,
  type CalendarRow,
} from '../../lib/publishing/calendarBuckets';
import {
  applyMovesToInput,
  applySelectionClick,
  EMPTY_SELECTION,
  excludedFromDrag,
  groupMovesByPost,
  isPostMovable,
  isRowMovable,
  isRowSelectable,
  planRowMoves,
  rowsForDrag,
  type RowMove,
  type SelectionState,
} from '../../lib/publishing/calendarDrag';
import { filterByDateRange, monthGridRange } from '../../lib/publishing/externalPosts';
import { externalCacheNote, externalStaleMs } from '../../lib/publishing/externalFreshness';
import {
  loadGroupMode,
  loadSelectedAccountIds,
  saveGroupMode,
  saveSelectedAccountIds,
  selectedAccountForCreate,
  type MenuGroupMode,
} from '../../lib/publishing/calendarSelection';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { PostMediaView } from '../../components/social/previews/PostMediaView';
import {
  deletePost,
  getPost,
  listExternalPosts,
  listPostsForAccounts,
  listPublishingAccounts,
  updatePost,
} from './api';
import { ConfirmDeletePostModal } from './ConfirmDeletePostModal';
import { PostStatsModal, type StatsTarget } from './PostStatsModal';
import { fromPost, toPostInput } from './composerState';
import { PublishingFilterRail, type PublishingFilters } from './PublishingFilterRail';
import { PublishingAccountRail } from './PublishingAccountRail';

// Publishing — the drag-drop content calendar. Reads the local posts mirror,
// month grid + an unscheduled draft tray.
//
// DRAG SEMANTICS (2026-07-20, superseding the original fan-out rule): a post
// fans out into one row per destination, and DRAGGING A ROW MOVES ONLY THAT
// DESTINATION — per-destination scheduling makes each row independently
// datable, so moving the whole post would silently drag destinations the AM
// didn't touch. Clicking a row still opens the composer with ALL destinations
// editable. Dragging a post out of the TRAY still schedules the whole post
// (nothing there has a date yet, so there is no single destination to mean).
//
// MULTI-SELECT (§F3): Ctrl/Cmd-click toggles a row, Shift-click extends a
// range; dragging any selected row moves the whole selection, preserving each
// row's time of day and their relative day offsets. Plain click is unchanged
// (open the post), so selection adds no mode.
//
// LAYOUT: a VIEWPORT-BOUNDED shell like the Community Inbox — useFullHeightLayout()
// flips AppShell to h-[100dvh], so the PAGE never scrolls and the month grid fills
// the height. Each column scrolls itself. GOTCHA: children of the scrolling rail
// MUST be shrink-0, or a panel's own overflow-hidden clips its rows.
//
// ═══ EXTERNAL POSTS (2026-07-20) ═══
// The calendar shows natively-published posts alongside ours, so content isn't
// double-booked. They are READ-ONLY throughout: no drag, no group-select, no edit
// — clicking one opens the stats view. See PostOrigin in types/publishing.ts.
//
// FETCH STRATEGY, and why it's scoped to one account:
//   • These are NOT mirrored locally (we don't own them; mirroring buys sync
//     complexity for a read-only display).
//   • `dateFrom` is IGNORED by Zernio, so we can't ask for just the visible month
//     — every call returns the account's whole retained history (300–400 days).
//   • That's ~2 round-trips per connected platform per account. Roster-wide on
//     every render (160+ accounts) is untenable, so external posts load ONLY when
//     an account filter is active.
// This also matches how the calendar is actually used — you plan content for one
// client at a time — so it's a product decision the performance ceiling agrees
// with, not a workaround. React Query caches per account with a long staleTime;
// the visible range is filtered client-side.
//
// ⚠️ COVERAGE LIMIT, stated in the UI: natively-SCHEDULED (future) posts are NOT
// retrievable — the status filter is ignored on the external collection and every
// observed row came back already-published. So this guards against double-booking
// against PUBLISHED HISTORY only. If someone schedules in Meta Business Suite, we
// cannot see it. A silent partial guarantee is worse than a stated limit.

const STATUS_STYLE: Record<PostStatus, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  publishing: 'bg-amber-50 text-amber-700 border-amber-200',
  published: 'bg-green-50 text-green-700 border-green-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

const EMPTY_FILTERS: PublishingFilters = {
  platforms: [],
  statuses: [],
  search: '',
  accountSearch: '',
};

// Drag payloads are PREFIXED so the drop target knows what it's receiving: a
// single destination row, or a whole undated post from the tray.
const ROW_PREFIX = 'row:';
const POST_PREFIX = 'post:';

// How long a fetched account's native-post history is considered fresh — now
// decided by externalStaleMs (src/lib/publishing/externalFreshness.ts, round 2
// item 5): 9h for PAST months (someone else's published history barely moves, and
// each fetch is several Zernio round-trips per platform), but 5 minutes while the
// visible grid contains TODAY, because "I just posted natively, is it there?" is
// the case people hit. The fetch can't be date-scoped (dateFrom is ignored), so
// the short lifetime keys on the visible range rather than on a "this week" query.
// The manual Refresh in the legend still re-pulls immediately.
//
// ⚠️ FLAG: this cache is downstream of Zernio's OWN native-sync lag — the unknown
// gap between a native post going live and listPosts({source:'external'}) returning
// it. Refresh re-pulls Zernio, but can't beat that outer lag; confirm the floor
// with Zernio (see the reply to Scott).

// External history is several Zernio round-trips PER account, so a big selection
// is genuinely expensive. We auto-fetch it for up to this many selected accounts;
// past the cap the legend offers a "load external history" affordance rather than
// firing dozens of round-trips unasked. Our own scheduled/draft posts always
// render immediately — external never blocks first paint.
const EXTERNAL_AUTO_CAP = 5;

export function PublishingCalendarPage() {
  useFullHeightLayout();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const accounts = useQuery({ queryKey: ['publishing-accounts'], queryFn: listPublishingAccounts });
  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts.data ?? []) m.set(a.id, a.name);
    return m;
  }, [accounts.data]);

  const today = new Date();
  const [month, setMonth] = useState({ year: today.getFullYear(), month0: today.getMonth() });
  // The SELECTION (session-persisted). The calendar shows nothing until it's
  // non-empty; the rail's Group-by mode is likewise persisted.
  const [selectedIds, setSelectedIdsState] = useState<Set<string>>(
    () => new Set(loadSelectedAccountIds()),
  );
  const [groupMode, setGroupModeState] = useState<MenuGroupMode>(() => loadGroupMode());
  const [filters, setFilters] = useState<PublishingFilters>(EMPTY_FILTERS);
  const [hover, setHover] = useState<HoverPreview | null>(null);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [statsTarget, setStatsTarget] = useState<StatsTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PostSummary | null>(null);
  const [loadAllExternal, setLoadAllExternal] = useState(false);

  const setSelectedIds = (next: Set<string>) => {
    setSelectedIdsState(next);
    saveSelectedAccountIds(next);
    setLoadAllExternal(false); // a new selection re-applies the external cap
  };
  const setGroupMode = (mode: MenuGroupMode) => {
    setGroupModeState(mode);
    saveGroupMode(mode);
  };

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const hasSelection = selectedIdList.length > 0;

  // SELECTION-SCOPED kyck posts: only the selected accounts (server chunks the ids
  // into ≤30 `in` batches). Nothing selected → no query, empty state.
  const posts = useQuery({
    queryKey: ['publishing-posts', 'scoped', [...selectedIdList].sort()],
    queryFn: () => listPostsForAccounts(selectedIdList),
    enabled: hasSelection,
  });

  // Natively-published posts — one query PER selected account (each 9h-cached),
  // auto-fetched up to EXTERNAL_AUTO_CAP (or all, once the affordance is used), so
  // a large selection doesn't fire dozens of Zernio round-trips or block the grid.
  const gridRange = useMemo(() => monthGridRange(month.year, month.month0), [month.year, month.month0]);
  const externalStale = externalStaleMs(gridRange, Date.now());
  const externalQueries = useQueries({
    queries: selectedIdList.map((id, i) => ({
      queryKey: ['publishing-external-posts', id],
      queryFn: () => listExternalPosts(id),
      enabled: loadAllExternal || i < EXTERNAL_AUTO_CAP,
      staleTime: externalStale,
    })),
  });

  const externalHiddenCount = loadAllExternal
    ? 0
    : Math.max(0, selectedIdList.length - EXTERNAL_AUTO_CAP);
  const externalSyncing = externalQueries.some((q) => q.isFetching);
  const externalCapped = externalQueries.some((q) => q.data?.capped);
  const externalFailures = useMemo(
    () => externalQueries.flatMap((q) => q.data?.failures ?? []),
    [externalQueries],
  );

  // The freshness escape hatch: re-pull every selected account's native history.
  const refreshExternal = () =>
    Promise.all(
      selectedIdList.map((id) =>
        qc.invalidateQueries({ queryKey: ['publishing-external-posts', id] }),
      ),
    );

  const filtered = useMemo(
    () =>
      filterPosts(posts.data ?? [], {
        // The fetch is already account-scoped; the filter still narrows by the
        // active platform/status/search facets on top of the selection.
        platforms: filters.platforms.length ? filters.platforms : undefined,
        statuses: filters.statuses.length ? filters.statuses : undefined,
        search: filters.search,
      }),
    [posts.data, filters],
  );

  // External rows (merged across the per-account queries) run through the SAME
  // filter, then a LOCAL date filter — Zernio ignores dateFrom, so the whole
  // retained history arrives and the visible range is applied here. The status
  // facet is deliberately NOT applied: these are always 'published' and filtering
  // to e.g. "drafts" should hide our drafts, not silently drop the published-
  // history context the AM is checking against.
  const externalRows = useMemo(
    () => externalQueries.flatMap((q) => q.data?.posts ?? []),
    [externalQueries],
  );
  const filteredExternal = useMemo(() => {
    const rows = filterPosts(externalRows, {
      platforms: filters.platforms.length ? filters.platforms : undefined,
      search: filters.search,
    });
    return filterByDateRange(rows, gridRange.from, gridRange.to);
  }, [externalRows, filters.platforms, filters.search, gridRange]);

  // One list for the grid; the tray stays kyck-only (an external post is never
  // undated, and could never be dragged out of a tray anyway).
  const calendarPosts = useMemo<CalendarPost[]>(
    () => [...filtered, ...filteredExternal],
    [filtered, filteredExternal],
  );
  // Each ROW is bucketed on its OWN effective date — a post whose destinations
  // run on different days appears in more than one cell.
  const buckets = useMemo(() => bucketRowsByDay(calendarPosts), [calendarPosts]);
  const tray = useMemo(() => unscheduledPosts(filtered), [filtered]);
  const grid = useMemo(
    () => buildMonthGrid(month.year, month.month0, today),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [month.year, month.month0],
  );

  // Every visible row, in reading order — what a Shift range follows and what a
  // group drag resolves against.
  const orderedRows = useMemo(
    () => grid.flat().flatMap((cell) => buckets.get(cell.key) ?? []),
    [grid, buckets],
  );
  const rowOrder = useMemo(() => orderedRows.map((r) => r.rowKey), [orderedRows]);
  const blockedFromDrag = useMemo(
    () => excludedFromDrag(orderedRows, selection.selected),
    [orderedRows, selection.selected],
  );

  const reschedule = useMutation({
    mutationFn: async (moves: RowMove[]) => {
      const byPost = groupMovesByPost(moves);
      const errors: string[] = [];
      for (const [postId, postMoves] of byPost) {
        const stored = await getPost(postId);
        // Force schedule mode BEFORE serialising, or toPostInput would drop the
        // per-destination times a draft is already carrying.
        const input = toPostInput({ ...fromPost(stored), publishMode: 'schedule' });
        const res = await updatePost(postId, applyMovesToInput(input, postMoves));
        if (res.dispatchError) errors.push(res.dispatchError);
      }
      return { rows: moves.length, posts: byPost.size, errors };
    },
    onSuccess: ({ rows, errors }) => {
      if (errors.length > 0) {
        toast.warning(`Moved locally — provider update failed: ${errors[0]}`);
      } else {
        toast.success(rows === 1 ? 'Moved' : `Moved ${rows} destinations`);
      }
      setSelection(EMPTY_SELECTION);
      qc.invalidateQueries({ queryKey: ['publishing-posts'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not reschedule'),
  });

  const remove = useMutation({
    mutationFn: (postId: string) => deletePost(postId),
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('Draft deleted');
      qc.invalidateQueries({ queryKey: ['publishing-posts'] });
    },
  });

  function handleDrop(payload: string, dayKey: string) {
    if (payload.startsWith(POST_PREFIX)) {
      // A whole undated draft from the tray — every destination lands together.
      const post = tray.find((p) => p.id === payload.slice(POST_PREFIX.length));
      if (!post) return;
      const rows = expandPostToRows(post);
      reschedule.mutate(planRowMoves(rows, rows[0]!.rowKey, dayKey));
      return;
    }
    if (!payload.startsWith(ROW_PREFIX)) return;
    const rowKey = payload.slice(ROW_PREFIX.length);
    const moving = rowsForDrag(orderedRows, selection.selected, rowKey);
    if (moving.length === 0) return;
    if (blockedFromDrag.length > 0) {
      toast.info(
        `${blockedFromDrag.length} published ${
          blockedFromDrag.length === 1 ? 'post is' : 'posts are'
        } read-only and stayed put.`,
      );
    }
    reschedule.mutate(planRowMoves(moving, rowKey, dayKey));
  }

  const rowByKey = useMemo(() => {
    const m = new Map<string, CalendarRow>();
    for (const r of orderedRows) m.set(r.rowKey, r);
    return m;
  }, [orderedRows]);

  function handleRowClick(rowKey: string, postId: string, e: React.MouseEvent) {
    const { state, openPost } = applySelectionClick(
      selection,
      rowKey,
      rowOrder,
      { toggle: e.ctrlKey || e.metaKey, range: e.shiftKey },
      // External rows can never enter a selection — gated in the pure layer so
      // there's no component-level oversight that could let one into a group drag.
      (key) => {
        const row = rowByKey.get(key);
        return row ? isRowSelectable(row) : false;
      },
    );
    setSelection(state);
    if (!openPost) return;

    // WHERE A PLAIN CLICK GOES:
    //   external      → stats (we don't own it; there is no composer to open)
    //   ours, published → stats (the composer is read-only for these anyway, and
    //                     "how did it do?" is the live question once it's out)
    //   ours, otherwise → the composer, unchanged
    const row = rowByKey.get(rowKey);
    const post = row?.post;
    if (post && (post.origin === 'external' || post.status === 'published')) {
      setStatsTarget({ post, accountName: accountName.get(post.kyckAccountId) ?? '' });
      return;
    }
    navigate(`/publishing/${postId}/edit`);
  }

  const monthLabel = new Date(month.year, month.month0, 1).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  // Pre-select the composer's account ONLY when exactly one is selected (else the
  // picker opens empty — the just-shipped day-icon rule).
  const createAccountId = selectedAccountForCreate(selectedIdList);
  // The external link icons — ONLY with exactly one account selected (Scott):
  // with several, whose website would they be?
  const soleAccountLinks = createAccountId
    ? accounts.data?.find((a) => a.id === createAccountId)?.links ?? null
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <PageHeader
          title="Content calendar"
          description="Schedule and manage posts across Facebook, Instagram and Google Business."
          actions={
            <div className="flex items-center gap-3">
              <AccountLinkIcons links={soleAccountLinks} />
              <Button asChild>
                {/* Carry the account into the composer ONLY when exactly one is
                    selected (unambiguous); else open with an empty picker. */}
                <Link to={createAccountId ? `/publishing/new?account=${createAccountId}` : '/publishing/new'}>
                  New post
                </Link>
              </Button>
            </div>
          }
        />
      </div>

      {selection.selected.size > 0 && (
        <div className="mb-2 flex shrink-0 items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-800">
          <span className="font-medium">
            {selection.selected.size} destination{selection.selected.size === 1 ? '' : 's'} selected
          </span>
          <span className="text-blue-600">Drag any one of them to move them together.</span>
          {blockedFromDrag.length > 0 && (
            <span className="text-amber-700">
              {blockedFromDrag.length} published — will stay put
            </span>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto h-6 px-2 text-xs"
            onClick={() => setSelection(EMPTY_SELECTION)}
          >
            Clear
          </Button>
        </div>
      )}

      {hasSelection && (
        <ExternalLegend
          count={filteredExternal.length}
          capped={externalCapped}
          failures={externalFailures}
          loading={externalQueries.some((q) => q.isLoading)}
          syncing={externalSyncing}
          hiddenCount={externalHiddenCount}
          onLoadAll={() => setLoadAllExternal(true)}
          onRefresh={refreshExternal}
          cacheNote={externalCacheNote(externalStale)}
        />
      )}

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="scroll-subtle flex w-64 min-h-0 shrink-0 flex-col gap-4 overflow-y-auto pb-2 pr-1">
          <PublishingFilterRail filters={filters} onChange={setFilters} />
          <PublishingAccountRail
            accounts={accounts.data ?? []}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            groupMode={groupMode}
            onGroupModeChange={setGroupMode}
            loading={accounts.isLoading}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-2 flex shrink-0 items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">{monthLabel}</h2>
            <div className="flex gap-1">
              <Button type="button" variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))}>
                ←
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMonth({ year: today.getFullYear(), month0: today.getMonth() })}
              >
                Today
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))}>
                →
              </Button>
            </div>
          </div>
          <div className="scroll-subtle min-h-0 flex-1 overflow-y-auto">
            {!hasSelection ? (
              <CalendarEmptyState />
            ) : posts.isLoading ? (
              <CenteredSpinner />
            ) : (
              <div className="grid h-full grid-cols-7 grid-rows-[auto_repeat(6,minmax(6rem,1fr))] gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d} className="bg-slate-50 px-2 py-1 text-center text-xs font-medium text-slate-500">
                    {d}
                  </div>
                ))}
                {grid.flat().map((cell) => (
                  <DayCell
                    key={cell.key}
                    cell={cell}
                    rows={buckets.get(cell.key) ?? []}
                    accountName={accountName}
                    activeAccountId={createAccountId ?? ''}
                    selected={selection.selected}
                    onDrop={(payload) => handleDrop(payload, cell.key)}
                    onRowClick={handleRowClick}
                    onHover={setHover}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Unscheduled tray — dragging from here schedules the WHOLE post. */}
        <div className="scroll-subtle flex w-56 min-h-0 shrink-0 flex-col overflow-y-auto pr-1">
          <h3 className="mb-2 shrink-0 text-sm font-semibold text-slate-700">
            Unscheduled ({tray.length})
          </h3>
          <div className="min-h-[120px] space-y-2 rounded-lg border border-dashed border-slate-200 p-2">
            {tray.length === 0 && (
              <p className="text-xs text-slate-400">Draft posts with no date appear here.</p>
            )}
            {tray.map((p) => (
              <PostChip
                key={p.id}
                post={p}
                accountName={accountName.get(p.kyckAccountId) ?? ''}
                onOpen={() => navigate(`/publishing/${p.id}/edit`)}
                onDelete={() => setDeleteTarget(p)}
              />
            ))}
          </div>
        </div>
      </div>

      {hover && <HoverPreviewCard preview={hover} />}
      <PostStatsModal target={statsTarget} onClose={() => setStatsTarget(null)} />
      {deleteTarget && (
        <ConfirmDeletePostModal
          scheduled={deleteTarget.zernioPostId !== null}
          busy={remove.isPending}
          errorText={remove.error instanceof Error ? remove.error.message : null}
          onConfirm={() => remove.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

/**
 * The external-post legend + its coverage limit.
 *
 * The limit is stated PLAINLY and permanently, not tucked in a tooltip: an AM
 * checking "has this already gone out?" needs to know the answer only covers
 * published history. Someone who assumes full coverage and schedules a clash has
 * been failed by the UI, not by their own carelessness.
 */
function ExternalLegend({
  count,
  capped,
  failures,
  loading,
  syncing,
  hiddenCount,
  onLoadAll,
  onRefresh,
  cacheNote,
}: {
  count: number;
  capped: boolean;
  failures: Array<{ platform: PostPlatform; message: string }>;
  // First load, no data yet — the count is replaced by a loading label.
  loading: boolean;
  // Any fetch in flight (incl. a manual Refresh over existing data) — shown as a
  // non-destructive "syncing" hint that KEEPS the current count visible.
  syncing: boolean;
  // Selected accounts BEYOND the external auto-fetch cap (0 = all loaded).
  hiddenCount: number;
  onLoadAll: () => void;
  onRefresh: () => void;
  // The cache said out loud (externalCacheNote): how long natively published
  // posts are kept before we re-ask, and that Refresh beats it.
  cacheNote: string;
}) {
  return (
    <div className="mb-2 shrink-0 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-1.5 text-xs text-violet-900">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 font-medium">
          <span className="inline-block h-2.5 w-2.5 rounded-sm border border-violet-300 bg-violet-100" />
          {loading ? 'Loading posted-natively content…' : `${count} posted natively`}
        </span>
        <span className="text-violet-700">
          Published outside Kyck — read-only. Click for stats.
        </span>
        {hiddenCount > 0 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 gap-1 px-2 text-xs text-violet-800 hover:bg-violet-100 hover:text-violet-900"
            onClick={onLoadAll}
            title={`Fetch native history for the remaining ${hiddenCount} selected account(s)`}
          >
            Load external history for {hiddenCount} more
          </Button>
        )}
        <span className="ml-auto flex items-center gap-2 text-violet-700">
          Scheduled-but-unpublished native posts aren&rsquo;t visible — only published history.
          {/* Manual re-pull: the long cache means a just-published native post
              won't appear until expiry, so this is the "I just posted, confirm
              it's there" escape hatch. Disabled + spinning while a fetch runs. */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 gap-1 px-2 text-xs text-violet-800 hover:bg-violet-100 hover:text-violet-900"
            onClick={onRefresh}
            disabled={syncing}
            title="Re-pull the selected accounts' native posts from the platform now"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', syncing && 'animate-spin')} />
            {syncing ? 'Syncing…' : 'Refresh'}
          </Button>
        </span>
      </div>
      <p className="mt-1 text-violet-700">{cacheNote}</p>
      {capped && (
        <p className="mt-1 text-violet-700">
          Showing the most recent posts only — this account&rsquo;s history is longer than the
          fetch limit.
        </p>
      )}
      {failures.length > 0 && (
        <p className="mt-1 text-amber-800">
          Couldn&rsquo;t load {failures.map((f) => f.platform).join(', ')}:{' '}
          {failures[0]!.message}
        </p>
      )}
    </div>
  );
}

// The calendar is selection-scoped: nothing renders until account(s) are picked.
function CalendarEmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 p-10 text-center">
      <CalendarPlus className="mb-3 h-8 w-8 text-slate-300" />
      <p className="text-sm font-medium text-slate-600">Select one or more locations</p>
      <p className="mt-1 max-w-sm text-xs text-slate-400">
        Pick accounts from the left to view and schedule their posts. Use “Group by” to browse
        by brand or owner group.
      </p>
    </div>
  );
}

function shiftMonth(m: { year: number; month0: number }, delta: number): { year: number; month0: number } {
  const d = new Date(m.year, m.month0 + delta, 1);
  return { year: d.getFullYear(), month0: d.getMonth() };
}

// The composer deep-link for "create a post on this day". Carries the active
// account filter EXACTLY like the header "New post" button (account param only
// when a filter is active), and always the clicked day as `date` (YYYY-MM-DD, the
// cell key) so the composer opens in Schedule mode pre-filled to that day.
// Exported for the day-cell render test.
export function newPostHref(date: string, accountId: string): string {
  const params = new URLSearchParams();
  if (accountId) params.set('account', accountId);
  params.set('date', date);
  return `/publishing/new?${params.toString()}`;
}

export function DayCell({
  cell,
  rows,
  accountName,
  activeAccountId,
  selected,
  onDrop,
  onRowClick,
  onHover,
}: {
  cell: CalendarCell;
  rows: CalendarRow[];
  accountName: Map<string, string>;
  activeAccountId: string;
  selected: ReadonlySet<string>;
  onDrop: (payload: string) => void;
  onRowClick: (rowKey: string, postId: string, e: React.MouseEvent) => void;
  onHover: (preview: HoverPreview | null) => void;
}) {
  const [over, setOver] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const overflowing = rows.length > MAX_CELL_ROWS;
  const visible = expanded || !overflowing ? rows : rows.slice(0, MAX_CELL_ROWS);
  const postCount = distinctPostCount(rows);

  return (
    <div
      className={`group flex min-h-0 flex-col overflow-hidden bg-white p-1 ${cell.inMonth ? '' : 'bg-slate-50/60'} ${
        over ? 'ring-2 ring-inset ring-blue-300' : ''
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const payload = e.dataTransfer.getData('text/plain');
        if (payload) onDrop(payload);
      }}
    >
      {/* Header row: a hover-revealed "create a post on this day" icon on the
          left (in the header corner so it never collides with the chips / their
          drag+click), the date number on the right. */}
      <div className="flex shrink-0 items-center justify-between">
        <Link
          to={newPostHref(cell.key, activeAccountId)}
          aria-label={`Create a post on ${cell.key}`}
          title="Create a post on this day"
          onClick={(e) => e.stopPropagation()}
          className="rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 group-hover:opacity-100"
        >
          <Plus className="h-3.5 w-3.5" />
        </Link>
        <span
          className={`text-[11px] ${cell.isToday ? 'font-bold text-blue-600' : 'text-slate-400'}`}
        >
          {cell.date.getDate()}
        </span>
      </div>
      <div className="scroll-subtle min-h-0 flex-1 space-y-0.5 overflow-y-auto">
        {visible.map((row) => (
          <CalendarRowChip
            key={row.rowKey}
            row={row}
            accountName={accountName.get(row.post.kyckAccountId) ?? ''}
            selected={selected.has(row.rowKey)}
            onClick={(e) => onRowClick(row.rowKey, row.postId, e)}
            onHover={onHover}
          />
        ))}
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 shrink-0 rounded px-1 py-0.5 text-left text-[10px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        >
          {expanded ? 'Show less' : `All (${postCount} post${postCount === 1 ? '' : 's'})`}
        </button>
      )}
    </div>
  );
}

/**
 * One fanned line: time · coloured platform glyph (+ placement when it differs
 * from a plain feed post) · truncated caption.
 *
 * ⚠️ The drag payload is this ROW's key, not the parent post id — a drag moves
 * ONE destination now. Exported for the drag regression test, which pins both
 * halves of that: the payload identifies the destination, and a plain click
 * still opens the parent post's single editor.
 */
export function CalendarRowChip({
  row,
  accountName,
  selected,
  onClick,
  onHover,
}: {
  row: CalendarRow;
  accountName: string;
  selected?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onHover: (preview: HoverPreview | null) => void;
}) {
  const post = row.post;
  const draggable = isRowMovable(row);
  const external = post.origin === 'external';
  const caption = post.content || '(no caption)';

  return (
    <button
      type="button"
      draggable={draggable}
      aria-pressed={selected}
      title={
        external
          ? 'Posted natively (outside Kyck) — read-only. Click for stats.'
          : undefined
      }
      onDragStart={(e) => {
        // Belt-and-braces: `draggable={false}` already stops the browser starting
        // a drag, but if one ever fires (a synthetic event, a future custom drag
        // layer) we refuse to put a payload on the clipboard. No payload, no drop.
        if (!draggable) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData('text/plain', `${ROW_PREFIX}${row.rowKey}`);
        onHover(null);
      }}
      onClick={onClick}
      onMouseEnter={(e) =>
        onHover({
          post,
          accountName,
          timeLabel: row.timeLabel,
          rect: e.currentTarget.getBoundingClientRect(),
        })
      }
      onMouseLeave={() => onHover(null)}
      className={cn(
        // Vertical stack: a compact metadata row on top, then the caption on its
        // OWN full-width lines below — so the description gets two full lines
        // instead of competing with the account-name prefix on line one.
        'flex w-full flex-col gap-0.5 rounded border px-1 py-0.5 text-left text-[11px]',
        // External posts get their OWN palette (violet + a dashed edge), not a
        // status colour: they must be distinguishable at a glance from anything
        // we own, and "published" green would put them in the same visual family
        // as our own published posts.
        external
          ? 'border-dashed border-violet-300 bg-violet-50 text-violet-900'
          : STATUS_STYLE[post.status],
        draggable ? 'cursor-grab' : 'cursor-pointer',
        selected && 'ring-2 ring-blue-500 ring-offset-1',
      )}
    >
      {/* metadata row: time · [drift •] · platform glyph · placement · account
          name (compact/muted, truncated to this one row) */}
      <span className="flex w-full items-center gap-1">
        {row.timeLabel && (
          <span className="shrink-0 font-medium tabular-nums opacity-70">{row.timeLabel}</span>
        )}
        {/* A destination on its own time is marked, so a post whose rows have
            visibly drifted apart is explicable rather than looking like a bug. */}
        {row.hasOwnTime && (
          <span className="shrink-0 opacity-60" title="This destination has its own time">
            •
          </span>
        )}
        {row.platform && <ChannelIcon channel={row.platform} className="h-3 w-3 shrink-0" />}
        {row.placementLabel && (
          <span className="shrink-0 rounded bg-white/70 px-1 text-[9px] font-semibold uppercase tracking-wide">
            {row.placementLabel}
          </span>
        )}
        {accountName && <span className="min-w-0 flex-1 truncate opacity-70">{accountName}</span>}
      </span>
      {/* caption on its own full-width lines */}
      <span className="w-full text-left line-clamp-2">{caption}</span>
    </button>
  );
}

interface HoverPreview {
  // CalendarPost: the hover card renders external rows too (caption + media are
  // common to both populations).
  post: CalendarPost;
  accountName: string;
  timeLabel: string;
  rect: DOMRect;
}

// Hover card — full caption + media. Rendered `fixed` at the hovered row's rect
// because the day cell clips its overflow, and `pointer-events-none` so it can
// never swallow a drag or a click.
function HoverPreviewCard({ preview }: { preview: HoverPreview }) {
  const WIDTH = 260;
  const left = Math.min(Math.max(8, preview.rect.left), window.innerWidth - WIDTH - 8);
  const below = preview.rect.bottom + 8;
  const fitsBelow = below + 280 < window.innerHeight;
  const style = fitsBelow
    ? { left, top: below }
    : { left, bottom: window.innerHeight - preview.rect.top + 8 };

  return (
    <div
      style={{ ...style, width: WIDTH }}
      className="pointer-events-none fixed z-50 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
    >
      <div className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800">
          {preview.accountName || 'Post'}
        </span>
        {preview.timeLabel && (
          <span className="shrink-0 text-[11px] text-slate-500">{preview.timeLabel}</span>
        )}
      </div>
      {preview.post.media.length > 0 && (
        <PostMediaView media={preview.post.media} aspectClass="aspect-square" />
      )}
      <p className="max-h-32 overflow-hidden whitespace-pre-wrap px-2 py-2 text-xs leading-snug text-slate-700">
        {preview.post.content || '(no caption)'}
      </p>
    </div>
  );
}

function PostChip({
  post,
  accountName,
  onOpen,
  onDelete,
}: {
  post: PostSummary;
  accountName: string;
  onOpen: () => void;
  onDelete: () => void;
}) {
  // Routed through the SHARED predicate rather than re-listing the statuses here.
  // The inline copy that used to live at this line would not have followed the
  // origin gate, so a rule change on the calendar would silently have missed the
  // tray — exactly the divergence that makes duplicated safety rules dangerous.
  const draggable = isPostMovable(post);
  // The tray holds only unscheduled DRAFTS, so a delete here is always a plain
  // local soft-delete. A container (not a <button>) so the hover Delete can be its
  // own button — a button can't nest inside a button.
  return (
    <div
      className={`group relative flex items-center rounded border text-[11px] ${STATUS_STYLE[post.status]}`}
    >
      <button
        type="button"
        draggable={draggable}
        onDragStart={(e) => {
          if (!draggable) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.setData('text/plain', `${POST_PREFIX}${post.id}`);
        }}
        onClick={onOpen}
        className={`block min-w-0 flex-1 truncate px-1.5 py-1 text-left ${
          draggable ? 'cursor-grab' : 'cursor-pointer'
        }`}
        title={post.content}
      >
        {accountName && <span className="font-medium">{accountName}: </span>}
        {post.content || '(no caption)'}
      </button>
      <button
        type="button"
        aria-label="Delete draft"
        title="Delete draft"
        onClick={onDelete}
        className="shrink-0 px-1 opacity-0 transition-opacity hover:text-red-600 focus:opacity-100 group-hover:opacity-100"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}
